import React, { FC, Fragment, NamedExoticComponent } from 'react'
import { YoldaIcons } from '../../../components/atoms/Icon/Icon'
import { Package } from '../../constants/Package'
import { ShipmentList, PackageSummary } from '../../constants/Trip'
import { Vehicle } from '../../constants/Vehicle'
import { VehicleBody } from '../../constants/VehicleBody'
import { Packages, Vehicles } from '../../../components/atoms'
import { ShipmentType } from '../../constants/Shipment'

type ShipmentDataMakerComponentsType = Record<string, FC | NamedExoticComponent>
type ShipmentDataMakerPropsType = Record<
  string,
  ({
    packageSummary,
    t,
    type,
    bodyType,
  }: {
    packageSummary: PackageSummary
    t: (arg: string) => string
    type: string
    bodyType: string
  }) => void
>

export const getLtlIcon = (type: string) => {
  switch (type) {
    case Package.Pallet:
      return '/assets/icons/MicroIcons/PalletMi.png'
    case Package.Box:
      return '/assets/icons/MicroIcons/BoxMi.png'
    case Package.Barrel:
      return '/assets/icons/MicroIcons/BarrelMi.png'
    case Package.Sack:
      return '/assets/icons/MicroIcons/SackMi.png'
    case Package.Other:
      return '/assets/icons/MicroIcons/OtherPackageMi.png'
    default:
      return '/assets/icons/MicroIcons/OtherPackageMi.png'
  }
}

export const getLtlOtherIcon = (type: string) => {
  switch (type) {
    case Package.Pallet2:
      return YoldaIcons.Pallet2
    case Package.BoxPallet2:
      return YoldaIcons.BoxPallet2
    case Package.Box2:
      return YoldaIcons.Box2
    case Package.Other2:
      return YoldaIcons.Other2
  }
}

export const getFtlIcon = (type: string) => {
  switch (type) {
    case Vehicle._10_Wheeler:
      return '/assets/icons/MicroIcons/VanMi.png'
    case Vehicle.SemiTruck:
      return '/assets/icons/MicroIcons/SemiTruckMi.png'
    case VehicleBody.FLATBED:
      return '/assets/icons/MicroIcons/FlatbedTrailerMi.png'
    case VehicleBody.TAUTLINER:
      return '/assets/icons/MicroIcons/TautlinerTrailerMi.png'
    case VehicleBody.STEEL:
      return '/assets/icons/MicroIcons/SteelTrailerMi.png'
    default:
      return '/assets/icons/MicroIcons/OtherVehicleMi.png'
  }
}

export const makePackageData = (packageSummary: PackageSummary, t: (arg: string) => string) => {
  return [
    ...(packageSummary?.barcodeEnabledPackages?.map?.(item => ({
      ...item,
      text: t(`Schema.packageTypes.${item?.type}`),
    })) || []),
    ...(packageSummary?.barcodeDisabledPackages?.map?.(item => ({
      ...item,
      text: t(`Schema.packageTypes.${item?.type}`),
    })) || []),
  ]
}

export const makeTripPackageData = (shipmentList: ShipmentList, t: (arg: string) => string) => {
  try {
    const packages = shipmentList.reduce(
      (
        acc: {
          height?: number
          length?: number
          number: number
          type: string
          weight?: number
          width?: number
        }[],
        item
      ) => {
        const arr = acc

        item?.packages?.forEach?.(packageItem => {
          const sameTypeIndex = arr.findIndex(arrItem => arrItem.type === packageItem.type)

          if (sameTypeIndex > -1) {
            return (arr[sameTypeIndex].number += packageItem?.number || 0)
          }

          return arr.push({
            type: packageItem.type || '',
            number: packageItem?.number || 0,
            width: packageItem?.width || 0,
            height: packageItem?.height || 0,
            length: packageItem?.length || 0,
            weight: packageItem?.weight || 0,
          })
        })

        return arr
      },
      []
    )

    return [
      ...(packages?.map?.(item => ({
        ...item,
        text: t(`Schema.packageTypes.${item?.type}`),
      })) || []),
    ]
  } catch {
    return []
  }
}

export const makeVehicleData = (type: string, bodyType: string, t: (arg: string) => string) => {
  return [
    {
      text: t(`Schema.VehicleTypes.${type}`),
      type,
    },
    {
      text: t(`Schema.VehicleBodyTypes.${bodyType}`),
      type: bodyType,
    },
  ]
}

const SHIPMENT_DATA_MAKER_COMPONENTS: ShipmentDataMakerComponentsType = {
  [ShipmentType.LTL]: Packages,
  [ShipmentType.FTL]: Vehicles,
}

const SHIPMENT_DATA_MAKER_PROPS: ShipmentDataMakerPropsType = {
  [ShipmentType.LTL]: ({ packageSummary, t }) => makePackageData(packageSummary, t),
  [ShipmentType.FTL]: ({ type, bodyType, t }) => makeVehicleData(type, bodyType, t),
}

export const makeShipmentData = (
  shipmentType: string,
  packageSummary: PackageSummary,
  vehicle: { type: string; bodyType: string },
  t: (arg: string) => string
) => {
  if (
    !shipmentType ||
    !(shipmentType in SHIPMENT_DATA_MAKER_PROPS) ||
    !(shipmentType in SHIPMENT_DATA_MAKER_COMPONENTS)
  ) {
    return { ShipmentDataComponent: Fragment, shipmentData: {} }
  }

  const Component = SHIPMENT_DATA_MAKER_COMPONENTS[shipmentType]

  const props = SHIPMENT_DATA_MAKER_PROPS[shipmentType]({
    t,
    packageSummary,
    type: vehicle?.type,
    bodyType: vehicle?.bodyType,
  })

  return { ShipmentDataComponent: Component, shipmentData: props }
}
